import { Box, Button, Flex, Stack } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'

import { MONTHS } from '@/utils/constants'
import normalizeBirthday from '@/utils/normalizeBirthday'
import parseNonQueryNumber from '@/utils/parseNonQueryNumber'

import { RPCRefresh } from '@/client/rpc'
import { BIRTH_YEARS } from '@/client/services/formInputs/onboarding'
import { getArticleUrl } from '@/client/services/url/faq'

import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Alert from '@/components/UI/Alert'
import Form from '@/components/UI/Form/Form'
import type { FormBlock, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

const FormFooter = ({ disabled, submitting }: { disabled: boolean; submitting: boolean }) => (
  <Box>
    <Flex direction="row" mt="36px" justifyContent="flex-end">
      <Button isDisabled={disabled} isLoading={submitting} type="submit">
        Submit
      </Button>
    </Flex>
  </Box>
)

export default function UpdateAgeModal({
  isOpen,
  onClose,
  hideVerificationNotice = false,
}: {
  isOpen: boolean
  onClose: () => void
  hideVerificationNotice?: boolean | null
}): React.ReactElement {
  const toast = useToaster()
  const formInputs: FormBlock[] = useMemo(() => {
    const verificationNotice: FormBlock = {
      type: 'content',
      display: 'display',
      name: 'warning_reset',
      content: (
        <Alert variant="warning">
          As soon as you update your birthday, you will need to verify your age before you can
          access live sessions again.{' '}
          <ShwLink href={getArticleUrl('age-verification-for-new-users')}>Learn more</ShwLink>
        </Alert>
      ),
      size: 6,
    }

    return [
      ...(hideVerificationNotice === true ? [] : [verificationNotice]),
      {
        label: 'Month',
        type: 'select',
        name: 'month_of_birth',
        required: true,
        options: MONTHS.map((item, index) => ({ label: item, value: `${index + 1}` })),
        size: 6,
      },
      {
        label: 'Year',
        type: 'select',
        name: 'year_of_birth',
        required: true,
        options: BIRTH_YEARS.map(item => ({ label: `${item}`, value: `${item}` })),
        size: 6,
      },
    ]
  }, [hideVerificationNotice])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      const dateOfBirth = normalizeBirthday(
        DateTime.fromObject({
          year: parseNonQueryNumber({ value: `${values.year_of_birth}`, defaultValue: 0 }),
          month: parseNonQueryNumber({ value: `${values.month_of_birth}`, defaultValue: 0 }),
        })
      ).toISO()

      await RPCRefresh.setMyAge({
        dateOfBirth,
      })

      toast({
        title: 'You have successfully updated your age.',
        status: 'success',
        duration: 30_000,
      })

      onClose()
    },
    [toast, onClose]
  )

  return (
    <ModalV3
      open={isOpen}
      size="md"
      onClose={onClose}
      title="Update Your Birthday"
      body={() => (
        <Stack direction="column" onSubmit={e => e.stopPropagation()}>
          <Form
            name="SafetyReport"
            blocks={formInputs}
            onSubmit={handleSubmit}
            FooterComponent={FormFooter}
          />
        </Stack>
      )}
    />
  )
}
